// Core
import React, { Suspense, useState } from 'react';
import { matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useIdContext } from '../context/VariableContext';
import { LanguageProvider } from '../layouts/v2/context/LanguageContext';
import { NotificationProvider } from '../layouts/v2/context/NotificationContext';
import { isSA, can } from '../layouts/v2/utils/userHierarchy';
import { isEmailBrandingAllowed } from '../layouts/v2/utils/emailBrandingPermission';
import { buildMfaRequiredUrl, MFA_ENROLL_PATH, MFA_GATE_CHECKING, MFA_GATE_REQUIRED } from '../layouts/v2/utils/mfa';
import { Loading } from '../Loanding';
import { useInView } from 'react-intersection-observer';
import { useSpring, animated } from '@react-spring/web';

// Layout y componentes base no lazy (se muestran siempre)
import { ErrorBoundary } from './ErrorBoundary';
import { NotFound } from '../components/page/notfound/NotFound';
import { RequireCoordinates, RequireNoPendingKits } from './guards';


// Componentes que se renderizan fuera de <Routes> (modales, overlays)
const UsersCreator = React.lazy(() => import('../components/page/private/equipo/UsersCreator').then(m => ({ default: m.UsersCreator })));
// V2 — overlay de kits pendientes sin Firestore directo ni SCSS
const ResponsiveKitsV2 = React.lazy(() => import('../layouts/v2/components/ResponsiveKitsV2').then(m => ({ default: m.ResponsiveKitsV2 })))
// Layout V2
const PrivateLayoutV2 = React.lazy(() => import('../layouts/v2/PrivateLayoutV2'));
// Shell de pantalla completa (providers V2 sin sidebar ni header): hoy solo /seguridad/doble-factor
const FullscreenLayoutV2 = React.lazy(() => import('../layouts/v2/components/FullscreenLayoutV2'));
const DashboardV2 = React.lazy(() => import('../pages/v2/DashboardV2'));
const MarcaV2 = React.lazy(() => import('../pages/v2/MarcaV2'));
// Notificaciones (personalizacion de correos de la compania). Ruta propia:
// NO confundir con NotificacionesV2 (/notificaciones, bandeja de la campana).
const NotificacionesCorreosV2 = React.lazy(() => import('../pages/v2/NotificacionesCorreosV2'));
const EncuestasV2 = React.lazy(() => import('../pages/v2/EncuestasV2'));
const SoporteV2 = React.lazy(() => import('../pages/v2/SoporteV2'));
const PerfilV2 = React.lazy(() => import('../pages/v2/PerfilV2'));
const DetalleEncuestaV2 = React.lazy(() => import('../pages/v2/DetalleEncuestaV2'));
const ResultadoEncuestaSinIaV2 = React.lazy(() => import('../pages/v2/ResultadoEncuestaSinIaV2'));
const ConversacionSoporteV2 = React.lazy(() => import('../pages/v2/ConversacionSoporteV2'));
const TiemposRespuestaSoporteV2 = React.lazy(() => import('../pages/v2/TiemposRespuestaSoporteV2'));
const CargaCsvV2 = React.lazy(() => import('../pages/v2/CargaCsvV2'));
const GestionEncuestaV2 = React.lazy(() => import('../pages/v2/GestionEncuestaV2'));
const NuevoUsuarioV2 = React.lazy(() => import('../pages/v2/NuevoUsuarioV2'));
const ManualesV2 = React.lazy(() => import('../pages/v2/ManualesV2'));
const ManualesAdminV2 = React.lazy(() => import('../pages/v2/ManualesAdminV2'));
const EquiposV2 = React.lazy(() => import('../pages/v2/EquiposV2'));
const GruposV2 = React.lazy(() => import('../pages/v2/GruposV2'));
const EditorGrupoV2 = React.lazy(() => import('../pages/v2/EditorGrupoV2'));
const EditorKitV2 = React.lazy(() => import('../pages/v2/EditorKitV2'));
const AsignarKitsV2 = React.lazy(() => import('../pages/v2/AsignarKitsV2'));
const ContestadorAsignacionesV2 = React.lazy(() => import('../pages/v2/ContestadorAsignacionesV2'));
const NotificacionesV2 = React.lazy(() => import('../pages/v2/NotificacionesV2'));
const DashboardAdminV2 = React.lazy(() => import('../pages/v2/DashboardAdminV2'));
const KitsListaV2 = React.lazy(() => import('../pages/v2/KitsListaV2'));
const MisKitsV2 = React.lazy(() => import('../pages/v2/MisKitsV2'));
const MisEquiposV2 = React.lazy(() => import('../pages/v2/MisEquiposV2'));
const MisEncuestasV2 = React.lazy(() => import('../pages/v2/MisEncuestasV2'));
const DashboardUserV2 = React.lazy(() => import('../pages/v2/DashboardUserV2'));
const MiResultadoEncuestaV2 = React.lazy(() => import('../pages/v2/MiResultadoEncuestaV2'));
const ColaboradoresV2 = React.lazy(() => import('../pages/v2/ColaboradoresV2'));
const ReportesV2 = React.lazy(() => import('../pages/v2/ReportesV2'));
const StockAsignadoV2 = React.lazy(() => import('../pages/v2/StockAsignadoV2'));
const StockPropioV2 = React.lazy(() => import('../pages/v2/StockPropioV2'));
// Inventario propio de AA-001 (S2-164)
const InventarioCategoriasV2 = React.lazy(() => import('../pages/v2/InventarioCategoriasV2'));
const FormularioInventarioCategoriaV2 = React.lazy(() => import('../pages/v2/FormularioInventarioCategoriaV2'));
const InventarioProductosV2 = React.lazy(() => import('../pages/v2/InventarioProductosV2'));
const FormularioInventarioProductoV2 = React.lazy(() => import('../pages/v2/FormularioInventarioProductoV2'));
const InventarioBodegasV2 = React.lazy(() => import('../pages/v2/InventarioBodegasV2'));
const FormularioInventarioBodegaV2 = React.lazy(() => import('../pages/v2/FormularioInventarioBodegaV2'));
const BalanceV2 = React.lazy(() => import('../pages/v2/BalanceV2'));
const ReporteReasignacionV2 = React.lazy(() => import('../pages/v2/ReporteReasignacionV2'));
const ReporteSaldoKitsV2 = React.lazy(() => import('../pages/v2/ReporteSaldoKitsV2'));
const CrearUsuarioV2 = React.lazy(() => import('../pages/v2/CrearUsuarioV2'));
const CargaMasivaUsuariosV2 = React.lazy(() => import('../pages/v2/CargaMasivaUsuariosV2'));
const EditarUsuarioV2 = React.lazy(() => import('../pages/v2/EditarUsuarioV2'));
// === DATA REPAIR FEATURE — eliminar cuando se termine la limpieza de datos ===
const DataRepairProductsV2 = React.lazy(() => import('../pages/v2/DataRepairProductsV2'));
const DataRepairUsersDatesV2 = React.lazy(() => import('../pages/v2/DataRepairUsersDatesV2'));
const DataRepairReassignmentsV2 = React.lazy(() => import('../pages/v2/DataRepairReassignmentsV2'));
const DataRepairUsersDatesDeleteV2 = React.lazy(() => import('../pages/v2/DataRepairUsersDatesDeleteV2'));
const DataRepairOrderDatesV2 = React.lazy(() => import('../pages/v2/DataRepairOrderDatesV2'));
const DataRepairOrdersDatesDeleteV2 = React.lazy(() => import('../pages/v2/DataRepairOrdersDatesDeleteV2'));
const DataRepairOrdersDatesV2 = React.lazy(() => import('../pages/v2/DataRepairOrdersDatesV2'));
const ReporteCreacionOrdenesV2 = React.lazy(() => import('../pages/v2/ReporteCreacionOrdenesV2'));
const ReporteBajaOrdenesV2 = React.lazy(() => import('../pages/v2/ReporteBajaOrdenesV2'));

const AsignarEncuestaV2 = React.lazy(() => import('../pages/v2/AsignarEncuestaV2'));
const ListaEncuestasAdminV2 = React.lazy(() => import('../pages/v2/ListaEncuestasAdminV2'));
const EditorEncuestaV2 = React.lazy(() => import('../pages/v2/EditorEncuestaV2'));
const CrearEncuestaV2 = React.lazy(() => import('../pages/v2/CrearEncuestaV2'));
const ResultadoDiagnosticoV2 = React.lazy(() => import('../pages/v2/ResultadoDiagnosticoV2'));
const ContestadorEncuestaV2 = React.lazy(() => import('../pages/v2/ContestadorEncuestaV2'));

const DashboardSAV2 = React.lazy(() => import('../pages/v2/DashboardSAV2'));
const CompaniasV2 = React.lazy(() => import('../pages/v2/CompaniasV2'));
const NuevaCompaniaV2 = React.lazy(() => import('../pages/v2/NuevaCompaniaV2'));
const EditarCompaniaV2 = React.lazy(() => import('../pages/v2/EditarCompaniaV2'));
const TransportadorasV2 = React.lazy(() => import('../pages/v2/TransportadorasV2'));
const NuevaTransportadoraV2 = React.lazy(() => import('../pages/v2/NuevaTransportadoraV2'));
const EditarTransportadoraV2 = React.lazy(() => import('../pages/v2/EditarTransportadoraV2'));
const MensajerosV2 = React.lazy(() => import('../pages/v2/MensajerosV2'));
const NuevoMensajeroV2 = React.lazy(() => import('../pages/v2/NuevoMensajeroV2'));
const EditarMensajeroV2 = React.lazy(() => import('../pages/v2/EditarMensajeroV2'));
const MovimientosLogisticosV2 = React.lazy(() => import('../pages/v2/MovimientosLogisticosV2'));
const TableroGastosLogisticosV2 = React.lazy(() => import('../pages/v2/TableroGastosLogisticosV2'));
const ConceptosGastoV2 = React.lazy(() => import('../pages/v2/ConceptosGastoV2'));
const GenerarCuentaPagoV2 = React.lazy(() => import('../pages/v2/GenerarCuentaPagoV2'));
const CuentasPagoV2 = React.lazy(() => import('../pages/v2/CuentasPagoV2'));
const BancosV2 = React.lazy(() => import('../pages/v2/BancosV2'));
const LogisticaSlaV2 = React.lazy(() => import('../pages/v2/LogisticaSlaV2'));
const NuevoConceptoGastoV2 = React.lazy(() => import('../pages/v2/NuevoConceptoGastoV2'));
const EditarConceptoGastoV2 = React.lazy(() => import('../pages/v2/EditarConceptoGastoV2'));
// Prefacturas (S2-107) — capability "billing": SA-001 / SA-002 / SA-003
const PrefacturasV2 = React.lazy(() => import('../pages/v2/PrefacturasV2'));
const PrefacturaDetalleV2 = React.lazy(() => import('../pages/v2/PrefacturaDetalleV2'));
const ContratosV2 = React.lazy(() => import('../pages/v2/ContratosV2'));
const NuevoContratoV2 = React.lazy(() => import('../pages/v2/NuevoContratoV2'));
const EditarContratoV2 = React.lazy(() => import('../pages/v2/EditarContratoV2'));
const CategoriasV2 = React.lazy(() => import('../pages/v2/CategoriasV2'));
const NuevaCategoriaV2 = React.lazy(() => import('../pages/v2/NuevaCategoriaV2'));
const EditarCategoriaV2 = React.lazy(() => import('../pages/v2/EditarCategoriaV2'));
const ProductosV2 = React.lazy(() => import('../pages/v2/ProductosV2'));
const NuevoProductoV2 = React.lazy(() => import('../pages/v2/NuevoProductoV2'));
const EditarProductoV2 = React.lazy(() => import('../pages/v2/EditarProductoV2'));
const SerializacionV2 = React.lazy(() => import('../pages/v2/SerializacionV2'));
const DetalleSerializacionV2 = React.lazy(() => import('../pages/v2/DetalleSerializacionV2'));
const OrdenesV2 = React.lazy(() => import('../pages/v2/OrdenesV2'));
const DetalleOrdenV2 = React.lazy(() => import('../pages/v2/DetalleOrdenV2'));
const ConfiguracionNotificacionesV2 = React.lazy(() => import('../pages/v2/ConfiguracionNotificacionesV2'));
const ConfiguracionSEOV2 = React.lazy(() => import('../pages/v2/ConfiguracionSEOV2'));
// Blog (S2-104) — capability "blogs": SA-001 / SA-005
const BlogsV2 = React.lazy(() => import('../pages/v2/BlogsV2'));
const NuevoBlogV2 = React.lazy(() => import('../pages/v2/NuevoBlogV2'));
const EditarBlogV2 = React.lazy(() => import('../pages/v2/EditarBlogV2'));
const BlogCategoriasV2 = React.lazy(() => import('../pages/v2/BlogCategoriasV2'));
const NuevaBlogCategoriaV2 = React.lazy(() => import('../pages/v2/NuevaBlogCategoriaV2'));
const EditarBlogCategoriaV2 = React.lazy(() => import('../pages/v2/EditarBlogCategoriaV2'));

// Rutas públicas (landing /p/:slug) — capability "publicRoutes": SA-001 / SA-005
const RutasPublicasV2 = React.lazy(() => import('../pages/v2/RutasPublicasV2'));
const NuevaRutaPublicaV2 = React.lazy(() => import('../pages/v2/NuevaRutaPublicaV2'));
const EditarRutaPublicaV2 = React.lazy(() => import('../pages/v2/EditarRutaPublicaV2'));

// Menú del footer (data-driven) — capability "publicRoutes": SA-001 / SA-005
const MenuFooterV2 = React.lazy(() => import('../pages/v2/MenuFooterV2'));
// Equipo "Nosotros" (data-driven) — capability "publicRoutes": SA-001 / SA-005
const EquipoTeamV2 = React.lazy(() => import('../pages/v2/EquipoTeamV2'));
// WhatsApp flotante (click-to-chat) — capability "publicRoutes": SA-001 / SA-005
const WhatsappFlotanteV2 = React.lazy(() => import('../pages/v2/WhatsappFlotanteV2'));
const AgentStudioV2 = React.lazy(() => import('../pages/v2/AgentStudioV2'));
const WorkflowStudioV2 = React.lazy(() => import('../pages/v2/WorkflowStudioV2'));
const CanalWhatsappConfigV2 = React.lazy(() => import('../pages/v2/CanalWhatsappConfigV2'));
const FormulariosPublicosV2 = React.lazy(() => import('../pages/v2/FormulariosPublicosV2'));
// S2-133 — Eventos: check-in + administración de reservas People Day Colombia 2026 (BUK)
const CheckinBuck2026V2 = React.lazy(() => import('../pages/v2/CheckinBuck2026V2'));
const ReservasBuck2026AdminV2 = React.lazy(() => import('../pages/v2/ReservasBuck2026AdminV2'));
// SDR retirado del router y del menú (2026-10-22, decisión de producto). Las páginas
// siguen en src/pages/v2/sdr/ sin enlazar; para reactivar, restaurar imports y rutas.
const AuditLogV2 = React.lazy(() => import('../pages/v2/AuditLogV2'));
// Estado de la plataforma (Cloud Monitoring + Error Reporting) — capability "platformHealth", solo SA-001
const PlatformHealthV2 = React.lazy(() => import('../pages/v2/PlatformHealthV2'));
// Doble factor TOTP (plan de seguridad, control 2b) — solo en la bifurcación SA (decisión 2026-10-26), sin can(),
// como pantalla completa fuera de PrivateLayoutV2 (2026-10-27)
const SeguridadDobleFactorV2 = React.lazy(() => import('../pages/v2/SeguridadDobleFactorV2'));

const V2Fallback = (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#667085' }}>
        Cargando...
    </div>
);

// Puerta del doble factor de los SA (FEAT-20261026-mfa-totp, sesión 5). AuthContext la decide al
// cargar el perfil (mfaGate). Un valor desconocido o ausente cuenta como "ok": el front nunca deja
// a un SA sin portal por su cuenta; quien exige el factor es el backend, en cada endpoint.
//  - "checking": todavía no se sabe. El mismo splash de AppRouter, sin layout, menú ni cabecera.
//  - "required": solo la pantalla de doble factor, a pantalla completa, para cualquier ruta.
const MfaGateChecking = (
    <div data-testid="mfa-gate-checking" role="status" aria-busy="true">
        <Loading />
    </div>
);

// matchPath es el matcher de <Routes>: acepta la barra final y la query.
const isMfaEnrollPath = (pathname) => Boolean(matchPath({ path: MFA_ENROLL_PATH, end: true }, pathname));

// Con la puerta cerrada (o sin decidir) un SA no ve nada del portal. En la propia pantalla de doble
// factor tampoco se pasa por los guards (dirección, kits pendientes): así, cuando la puerta pasa de
// "required" a "ok" tras configurar, el árbol no cambia y la pantalla conserva su estado.
const saSkipsPortalGuards = (user, mfaGate, pathname) =>
    isSA(user?.rol) && (mfaGate === MFA_GATE_CHECKING || mfaGate === MFA_GATE_REQUIRED || isMfaEnrollPath(pathname));

const FadeInSection = ({ children }) => {
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const props = useSpring({
        opacity: inView ? 1 : 0,
        from: { opacity: 0 },
        config: { duration: 500 },
    });
    return <animated.div style={props} ref={ref}>{children}</animated.div>;
};

/**
 * Fallback cuando el usuario no tiene coordenadas registradas.
 * Pide la dirección antes de seguir.
 */
const MissingCoordinatesFallback = ({ user, setLoad, setIsOpenCreate }) => (
    <div className="open-create">
        <FadeInSection>
            <section className="section-open-create">
                <h2 className='subtitle2 space-bottom'>Verifica, por favor, tu dirección para continuar</h2>
                <Suspense fallback={V2Fallback}>
                    <UsersCreator
                        idc={user?.idc}
                        setLoad={setLoad}
                        setIsOpenCreate={setIsOpenCreate}
                        idItem={user?.id}
                        location_cordena={user?.location_cordena}
                    />
                </Suspense>
            </section>
        </FadeInSection>
    </div>
);

/**
 * Fallback cuando hay kits pendientes que el usuario debe atender.
 * Usa ResponsiveKitsV2 (Tailwind, sin Firestore directo, sin SCSS).
 * ResponsiveKits legacy sigue importado más arriba para rollback rápido.
 */
const PendingKitsFallback = (pendingKits, onSkip) => (
    <Suspense fallback={V2Fallback}>
        <LanguageProvider>
            <NotificationProvider>
                <ResponsiveKitsV2 pendingKits={pendingKits} onSkip={onSkip} />
            </NotificationProvider>
        </LanguageProvider>
    </Suspense>
);

const PrivateRouterInner = () => {
    const { user, mfaGate } = useAuth();
    const location = useLocation();

    const {
        rol = null,
    } = user || {};

    // Permiso de compañía para la personalización de correos (S2-126b).
    // `company.emailBrandingEnabled` viaja dentro del user porque AuthContext
    // hace spread del doc de company; ausente = denegado. Igual que con
    // `can()`, esto sólo evita montar la ruta: el enforcement real es el 403
    // del backend (PUT /companyProfileV2/company/email-branding).
    const emailBrandingAllowed = isEmailBrandingAllowed(user);


    // Bifurcación V2 - Super Admin (SA-001..SA-005), unificada por permisos.
    // El backend es la fuente de verdad: hidrata `user.permissions` vía
    // GET /usersV2/my-permissions. Aquí solo gateamos las <Route> con `can()`.
    // El enforcement real sigue en backend (requireCapability). Si los permisos
    // aún no cargaron (undefined), se tratan como {} y solo quedan las rutas
    // personales (/, /perfil, /notificaciones).
    if (isSA(rol)) {
        // Puerta del doble factor sin decidir: ni layout, ni menú, ni dashboard.
        if (mfaGate === MFA_GATE_CHECKING) return MfaGateChecking;

        // Doble factor TOTP (control 2b): pantalla completa, FUERA de PrivateLayoutV2 (sin
        // sidebar, header ni menú de perfil). Solo SA y sin can(): tiene que abrir aunque los
        // permisos no hayan cargado y el backend responda MFA_REQUIRED a todo.
        //  - Puerta en "required": es la ÚNICA pantalla del portal, para cualquier ruta. Una URL
        //    distinta se reemplaza (sin recargar) por la de la pantalla con ?mfa=requerido.
        //  - Puerta en "ok": solo en su ruta; cualquier otra sigue dentro del layout.
        // El mismo árbol en los dos casos: al pasar de "required" a "ok" tras configurar, la
        // pantalla no se vuelve a montar.
        if (mfaGate === MFA_GATE_REQUIRED || isMfaEnrollPath(location.pathname)) {
            return (
                <Suspense fallback={V2Fallback}>
                    <FullscreenLayoutV2>
                        <Routes>
                            <Route path={MFA_ENROLL_PATH} element={<SeguridadDobleFactorV2 />} />
                            <Route path='*' element={<Navigate to={buildMfaRequiredUrl()} replace />} />
                        </Routes>
                    </FullscreenLayoutV2>
                </Suspense>
            );
        }

        const perms = user?.permissions || {};
        return (
            <Suspense fallback={V2Fallback}>
                <PrivateLayoutV2 user={user}>
                    <Routes>
                        {/* Home + personales: siempre para todo SA */}
                        <Route path='/' element={<DashboardSAV2 user={user} />} />
                        <Route path='/perfil' element={<PerfilV2 user={user} />} />
                        {/* /seguridad/doble-factor no va aquí: se resuelve arriba, como pantalla completa */}
                        <Route path='/notificaciones' element={<NotificacionesV2 />} />

                        {/* Usuarios */}
                        {can(perms, 'users', 'view') && <Route path='/usuarios' element={<ColaboradoresV2 />} />}
                        {can(perms, 'users', 'view') && <Route path='/usuarios/:id' element={<EditarUsuarioV2 />} />}
                        {can(perms, 'users', 'create') && <Route path='/usuarios/nuevo' element={<CrearUsuarioV2 />} />}

                        {/* Compañías */}
                        {can(perms, 'companies', 'view') && <Route path='/admin/companys' element={<CompaniasV2 />} />}
                        {can(perms, 'companies', 'view') && <Route path='/admin/companys/:id' element={<EditarCompaniaV2 />} />}
                        {can(perms, 'companies', 'create') && <Route path='/admin/companys/new' element={<NuevaCompaniaV2 />} />}

                        {/* Transportadoras */}
                        {can(perms, 'carriers', 'view') && <Route path='/admin/transportadoras' element={<TransportadorasV2 />} />}
                        {can(perms, 'carriers', 'view') && <Route path='/admin/transportadoras/:id' element={<EditarTransportadoraV2 />} />}
                        {can(perms, 'carriers', 'create') && <Route path='/admin/transportadoras/new' element={<NuevaTransportadoraV2 />} />}

                        {/* Mensajeros */}
                        {can(perms, 'messengers', 'view') && <Route path='/admin/mensajeros' element={<MensajerosV2 />} />}
                        {can(perms, 'messengers', 'view') && <Route path='/admin/mensajeros/:id' element={<EditarMensajeroV2 />} />}
                        {can(perms, 'messengers', 'create') && <Route path='/admin/mensajeros/new' element={<NuevoMensajeroV2 />} />}

                        {/* Control de gastos logísticos (movimientos + tablero + conceptos) */}
                        {can(perms, 'logisticsExpenses', 'view') && <Route path='/admin/movimientos-logisticos' element={<MovimientosLogisticosV2 />} />}
                        {can(perms, 'logisticsExpenses', 'view') && <Route path='/admin/gastos-logisticos/tablero' element={<TableroGastosLogisticosV2 />} />}
                        {can(perms, 'logisticsExpenses', 'view') && <Route path='/admin/conceptos-gasto' element={<ConceptosGastoV2 />} />}
                        {can(perms, 'logisticsExpenses', 'view') && <Route path='/admin/conceptos-gasto/:id' element={<EditarConceptoGastoV2 />} />}
                        {can(perms, 'logisticsExpenses', 'edit') && <Route path='/admin/conceptos-gasto/new' element={<NuevoConceptoGastoV2 />} />}
                        {can(perms, 'logisticsExpenses', 'view') && <Route path='/admin/generar-cuenta-pago' element={<GenerarCuentaPagoV2 />} />}
                        {can(perms, 'logisticsExpenses', 'view') && <Route path='/admin/cuentas-pago' element={<CuentasPagoV2 />} />}
                        {can(perms, 'logisticsExpenses', 'view') && <Route path='/admin/bancos' element={<BancosV2 />} />}
                        {can(perms, 'logisticsSla', 'view') && <Route path='/admin/sla-territorios' element={<LogisticaSlaV2 />} />}

                        {/* Contratos (assignment) */}
                        {can(perms, 'contracts', 'view') && <Route path='/admin/assignment' element={<ContratosV2 />} />}
                        {can(perms, 'contracts', 'view') && <Route path='/admin/assignment/:id' element={<EditarContratoV2 />} />}
                        {can(perms, 'contracts', 'create') && <Route path='/admin/assignment/new' element={<NuevoContratoV2 />} />}

                        {/* Prefacturas (S2-107) — capability "billing" */}
                        {can(perms, 'billing', 'view') && <Route path='/admin/prefacturas' element={<PrefacturasV2 />} />}
                        {can(perms, 'billing', 'view') && <Route path='/admin/prefacturas/:id' element={<PrefacturaDetalleV2 />} />}

                        {/* Categorías */}
                        {can(perms, 'categories', 'view') && <Route path='/admin/categories' element={<CategoriasV2 />} />}
                        {can(perms, 'categories', 'view') && <Route path='/admin/categories/:id' element={<EditarCategoriaV2 />} />}
                        {can(perms, 'categories', 'create') && <Route path='/admin/categories/new' element={<NuevaCategoriaV2 />} />}

                        {/* Productos */}
                        {can(perms, 'products', 'view') && <Route path='/admin/products' element={<ProductosV2 />} />}
                        {can(perms, 'products', 'view') && <Route path='/admin/products/:id' element={<EditarProductoV2 />} />}
                        {can(perms, 'products', 'create') && <Route path='/admin/products/new' element={<NuevoProductoV2 />} />}

                        {/* Serialización */}
                        {can(perms, 'serials', 'view') && <Route path='/admin/serials' element={<SerializacionV2 />} />}
                        {can(perms, 'serials', 'view') && <Route path='/admin/serials/:id' element={<DetalleSerializacionV2 />} />}

                        {/* Órdenes */}
                        {can(perms, 'orders', 'view') && <Route path='/admin/orders' element={<OrdenesV2 />} />}
                        {can(perms, 'orders', 'view') && <Route path='/admin/orders/:id' element={<DetalleOrdenV2 />} />}

                        {/* Soporte */}
                        {can(perms, 'support', 'view') && <Route path='/soporte' element={<SoporteV2 />} />}
                        {/* S2-147: informe de tiempos de respuesta (solo SA) */}
                        {can(perms, 'support', 'view') && <Route path='/soporte/tiempos-respuesta' element={<TiemposRespuestaSoporteV2 />} />}
                        {can(perms, 'support', 'view') && <Route path='/soporte/:id' element={<ConversacionSoporteV2 />} />}

                        {/* Configuraciones */}
                        {can(perms, 'notifConfig', 'view') && <Route path='/admin/notificaciones-config' element={<ConfiguracionNotificacionesV2 />} />}
                        {can(perms, 'seoConfig', 'view') && <Route path='/admin/configuracion-seo' element={<ConfiguracionSEOV2 />} />}
                        {can(perms, 'publicForms', 'view') && <Route path='/admin/formularios-publicos' element={<FormulariosPublicosV2 />} />}

                        {/* Eventos (S2-133) — capability "eventReservations" */}
                        {can(perms, 'eventReservations', 'process') && <Route path='/admin/eventos/buck2026/checkin' element={<CheckinBuck2026V2 />} />}
                        {can(perms, 'eventReservations', 'view') && <Route path='/admin/eventos/buck2026/reservas' element={<ReservasBuck2026AdminV2 />} />}

                        {/* Blog (contenido global) — capability "blogs" */}
                        {can(perms, 'blogs', 'create') && <Route path='/admin/blogs/new' element={<NuevoBlogV2 />} />}
                        {can(perms, 'blogs', 'view') && <Route path='/admin/blogs/:id' element={<EditarBlogV2 />} />}
                        {can(perms, 'blogs', 'view') && <Route path='/admin/blogs' element={<BlogsV2 />} />}
                        {can(perms, 'blogs', 'create') && <Route path='/admin/blog-categorias/new' element={<NuevaBlogCategoriaV2 />} />}
                        {can(perms, 'blogs', 'view') && <Route path='/admin/blog-categorias/:id' element={<EditarBlogCategoriaV2 />} />}
                        {can(perms, 'blogs', 'view') && <Route path='/admin/blog-categorias' element={<BlogCategoriasV2 />} />}

                        {/* Rutas públicas (contenido global) — capability "publicRoutes" */}
                        {can(perms, 'publicRoutes', 'create') && <Route path='/admin/rutas-publicas/new' element={<NuevaRutaPublicaV2 />} />}
                        {can(perms, 'publicRoutes', 'view') && <Route path='/admin/rutas-publicas/:id' element={<EditarRutaPublicaV2 />} />}
                        {can(perms, 'publicRoutes', 'view') && <Route path='/admin/rutas-publicas' element={<RutasPublicasV2 />} />}

                        {/* Menú del footer (data-driven) — capability "publicRoutes" */}
                        {can(perms, 'publicRoutes', 'view') && <Route path='/admin/menu-footer' element={<MenuFooterV2 />} />}

                        {/* Equipo "Nosotros" (data-driven) — capability "publicRoutes" */}
                        {can(perms, 'publicRoutes', 'view') && <Route path='/admin/equipo-nosotros' element={<EquipoTeamV2 />} />}

                        {/* WhatsApp flotante (click-to-chat) — capability "publicRoutes" */}
                        {can(perms, 'publicRoutes', 'view') && <Route path='/admin/whatsapp-flotante' element={<WhatsappFlotanteV2 />} />}

                        {/* Agent Studio + Workflow Studio (aiStudio) */}
                        {can(perms, 'aiStudio', 'view') && <Route path='/admin/agent-studio' element={<AgentStudioV2 />} />}
                        {can(perms, 'aiStudio', 'view') && <Route path='/admin/workflow-studio' element={<WorkflowStudioV2 />} />}
                        {can(perms, 'aiStudio', 'view') && <Route path='/admin/canal-whatsapp' element={<CanalWhatsappConfigV2 />} />}

                        {/* Bitácora de auditoría (B3 Siigo) — capability "auditLog", cross-idc */}
                        {can(perms, 'auditLog', 'view') && <Route path='/auditoria' element={<AuditLogV2 />} />}

                        {/* Estado de la plataforma — capability "platformHealth" (solo SA-001); el 403 real lo da /platformHealthV2 */}
                        {can(perms, 'platformHealth', 'view') && <Route path='/admin/estado-plataforma' element={<PlatformHealthV2 />} />}

                        <Route path='*' element={<NotFound home="/" />} />
                    </Routes>
                </PrivateLayoutV2>
            </Suspense>
        );
    }

    // Bifurcación V2 - US-001
    // Migra el flujo de "Mis kits" (legacy ListAsignaciones) al stack V2.
    // Reutiliza pantallas V2 ya existentes para /perfil, /notificaciones,
    // /soporte y /manuales. ContestadorAsignacionesV2 se mantiene como detalle
    // del kit asignado (sigue consumiendo los contextos legacy montados en
    // AppProvider, que persisten para todos los roles).
    if (rol === 'US-001') {
        // Rutas fullscreen (sin sidebar/navbar): el contestador de encuesta
        const isFullscreenUS001 = /^\/encuestas\/contestar\/.+/.test(location.pathname);

        if (isFullscreenUS001) {
            return (
                <Suspense fallback={V2Fallback}>
                    <LanguageProvider>
                        <NotificationProvider>
                            <Routes>
                                <Route path='/encuestas/contestar/:id' element={<ContestadorEncuestaV2 />} />
                                <Route path='*' element={<NotFound home="/" />} />
                            </Routes>
                        </NotificationProvider>
                    </LanguageProvider>
                </Suspense>
            );
        }

        return (
            <Suspense fallback={V2Fallback}>
                <PrivateLayoutV2 user={user}>
                    <Routes>
                        <Route path='/' element={<DashboardUserV2 />} />
                        <Route path='/kits/lista' element={<MisKitsV2 />} />
                        <Route path='/kits/asignados/:id' element={<ContestadorAsignacionesV2 />} />
                        <Route path='/equipos' element={<MisEquiposV2 />} />
                        <Route path='/encuesta/lista' element={<MisEncuestasV2 />} />
                        <Route path='/encuesta/resultado/:id' element={<MiResultadoEncuestaV2 />} />
                        <Route path='/perfil' element={<PerfilV2 user={user} />} />
                        <Route path='/notificaciones' element={<NotificacionesV2 />} />
                        <Route path='/soporte' element={<SoporteV2 />} />
                        <Route path='/soporte/:id' element={<ConversacionSoporteV2 />} />
                        <Route path='/manuales' element={<ManualesV2 />} />
                        <Route path='*' element={<NotFound home="/" />} />
                    </Routes>
                </PrivateLayoutV2>
            </Suspense>
        );
    }

    // Bifurcación V2 - AA-001
    if (rol === 'AA-001') {
        // Rutas fullscreen (sin sidebar/navbar) dentro de la bifurcación V2
        const isFullscreenAA001 = /^\/encuestas\/contestar\/.+/.test(location.pathname);

        if (isFullscreenAA001) {
            return (
                <Suspense fallback={V2Fallback}>
                    <LanguageProvider>
                        <NotificationProvider>
                            <Routes>
                                <Route path='/encuestas/contestar/:id' element={<ContestadorEncuestaV2 />} />
                                <Route path='*' element={<NotFound home="/" />} />
                            </Routes>
                        </NotificationProvider>
                    </LanguageProvider>
                </Suspense>
            );
        }

        return (
            <Suspense fallback={V2Fallback}>
                <PrivateLayoutV2 user={user}>
                    <Routes>
                        {/* Rutas activas */}
                        <Route path='/' element={<DashboardAdminV2 user={user} />} />
                        <Route path='/usuarios' element={<ColaboradoresV2 />} />
                        <Route path='/usuarios/nuevo' element={<CrearUsuarioV2 />} />
                        <Route path='/usuarios/:id' element={<EditarUsuarioV2 />} />
                        <Route path='/usuarios/carga-masiva' element={<CargaMasivaUsuariosV2 />} />
                        <Route path='/kits/lista' element={<MisKitsV2 />} />
                        <Route path='/kits/asignados/:id' element={<ContestadorAsignacionesV2 />} />
                        <Route path='/reportes' element={<ReportesV2 />} />
                        <Route path='/reportes/reasignaciones' element={<ReporteReasignacionV2 />} />
                        <Route path='/reportes/creacion-ordenes' element={<ReporteCreacionOrdenesV2 />} />
                        <Route path='/reportes/baja-ordenes' element={<ReporteBajaOrdenesV2 />} />
                        <Route path='/reportes/saldo-kits' element={<ReporteSaldoKitsV2 />} />
                        <Route path='/inventario/asignado' element={<StockAsignadoV2 />} />
                        <Route path='/inventario/propio' element={<StockPropioV2 />} />
                        {/* Inventario propio (S2-164): solo AA-001. El enforcement real es
                            requireAA001 en /companyInventoryV2; aquí solo se gatea la ruta. */}
                        <Route path='/inventario-propio/categorias' element={<InventarioCategoriasV2 />} />
                        <Route path='/inventario-propio/categorias/nueva' element={<FormularioInventarioCategoriaV2 />} />
                        <Route path='/inventario-propio/categorias/:id' element={<FormularioInventarioCategoriaV2 />} />
                        <Route path='/inventario-propio/productos' element={<InventarioProductosV2 />} />
                        <Route path='/inventario-propio/productos/nuevo' element={<FormularioInventarioProductoV2 />} />
                        <Route path='/inventario-propio/productos/:id' element={<FormularioInventarioProductoV2 />} />
                        <Route path='/inventario-propio/bodegas' element={<InventarioBodegasV2 />} />
                        <Route path='/inventario-propio/bodegas/nueva' element={<FormularioInventarioBodegaV2 />} />
                        <Route path='/inventario-propio/bodegas/:id' element={<FormularioInventarioBodegaV2 />} />
                        <Route path='/movimientos/balance' element={<BalanceV2 />} />
                        <Route path='/soporte' element={<SoporteV2 />} />
                        <Route path='/soporte/:id' element={<ConversacionSoporteV2 />} />
                        <Route path='/perfil' element={<PerfilV2 user={user} />} />
                        <Route path='/notificaciones' element={<NotificacionesV2 />} />
                        <Route path='/manuales' element={<ManualesV2 />} />
                        {/* PR2: Encuestas */}
                        <Route path='/encuestas/send' element={<AsignarEncuestaV2 />} />
                        <Route path='/encuesta/lista' element={<ListaEncuestasAdminV2 />} />
                        <Route path='/encuestas/creador' element={<CrearEncuestaV2 />} />
                        <Route path='/encuestas/diagnostic/:id' element={<ResultadoDiagnosticoV2 />} />
                        <Route path='/encuestas/:id' element={<EditorEncuestaV2 />} />
                        {/* PR3: Kits */}
                        <Route path='/kits/send' element={<AsignarKitsV2 />} />
                        <Route path='/kits/creador' element={<EditorKitV2 />} />
                        <Route path='/kits/creador/:id' element={<EditorKitV2 />} />
                        <Route path='/equipos' element={<EquiposV2 />} />
                        <Route path='/manuales/:route' element={<NotFound home="/" />} />
                        {/* PR4: Grupos */}
                        <Route path='/usuarios/grupos' element={<GruposV2 />} />
                        <Route path='/usuarios/grupos/creador' element={<EditorGrupoV2 />} />
                        <Route path='/usuarios/grupos/:id' element={<EditorGrupoV2 />} />
                        <Route path='/marca' element={<MarcaV2 user={user} />} />
                        {emailBrandingAllowed && <Route path='/notificaciones/correos' element={<NotificacionesCorreosV2 />} />}
                        {/* Diagnóstico ergonómico — mismo módulo que AA-002 (C000003).
                            AA-001 lo tiene porque una compañía convertida de C000003 a
                            C000001 conserva su histórico de users_open/quizanswer_open
                            bajo el mismo idc. Se reutilizan los MISMOS paths que la
                            bifurcación AA-002: las pantallas navegan entre sí con
                            '/open/invitations/...' hardcodeado. */}
                        <Route path='/open/invitations' element={<EncuestasV2 />} />
                        {/* Vista consolidada: es la home de AA-002 (DashboardV2). En
                            AA-001 la home ya la ocupa DashboardAdminV2, así que vive
                            como ruta propia dentro del módulo. */}
                        <Route path='/open/invitations/consolidado' element={<DashboardV2 user={user} />} />
                        <Route path='/open/invitations/new' element={<NuevoUsuarioV2 />} />
                        <Route path='/open/invitations/csv' element={<CargaCsvV2 />} />
                        <Route path='/open/invitations/edit/:id' element={<GestionEncuestaV2 />} />
                        <Route path='/open/invitations/:id' element={<DetalleEncuestaV2 />} />
                        {/* Bitácora de auditoría (B3 Siigo) — solo su idc (lo fuerza el backend) */}
                        <Route path='/auditoria' element={<AuditLogV2 />} />
                        {/* Catch-all */}
                        <Route path='*' element={<NotFound home="/" />} />
                    </Routes>
                </PrivateLayoutV2>
            </Suspense>
        );
    }

    // Bifurcación V2 - AA-002 / US-005 (Visualizador, mismas rutas pero permisos restringidos)
    if (rol === 'AA-002' || rol === 'US-005') {
        return (
            <Suspense fallback={V2Fallback}>
                <PrivateLayoutV2 user={user}>
                    <Routes>
                        <Route path='/marca' element={<MarcaV2 user={user} />} />
                        {emailBrandingAllowed && <Route path='/notificaciones/correos' element={<NotificacionesCorreosV2 />} />}
                        <Route path='/open/invitations' element={<EncuestasV2 />} />
                        {/* US-005 (visualizador) ve el resultado SIN IA ni edición; AA-002 ve el detalle completo */}
                        <Route path='/open/invitations/:id' element={rol === 'US-005' ? <ResultadoEncuestaSinIaV2 /> : <DetalleEncuestaV2 />} />
                        <Route path='/open/invitations/csv' element={<CargaCsvV2 />} />
                        <Route path='/open/invitations/edit/:id' element={<GestionEncuestaV2 />} />
                        <Route path='/open/invitations/new' element={<NuevoUsuarioV2 />} />
                        <Route path='/soporte' element={<SoporteV2 />} />
                        <Route path='/soporte/:id' element={<ConversacionSoporteV2 />} />
                        <Route path='/manuales' element={<ManualesV2 />} />
                        <Route path='/notificaciones' element={<NotificacionesV2 />} />
                        <Route path='/perfil' element={<PerfilV2 user={user} />} />
                        <Route path='/' element={<DashboardV2 user={user} />} />
                        <Route path='*' element={<NotFound home="/" />} />
                    </Routes>
                </PrivateLayoutV2>
            </Suspense>
        );
    }

    // Bifurcación V2 - AA-003
    if (rol === 'AA-003') {
        return (
            <Suspense fallback={V2Fallback}>
                <PrivateLayoutV2 user={user}>
                    <Routes>
                        <Route path='/marca' element={<MarcaV2 user={user} />} />
                        {emailBrandingAllowed && <Route path='/notificaciones/correos' element={<NotificacionesCorreosV2 />} />}
                        <Route path='/kits/lista' element={<KitsListaV2 />} />
                        <Route path='/kits/detalle/:id' element={<KitsListaV2 />} />
                        <Route path='/usuarios' element={<ColaboradoresV2 />} />
                        <Route path='/usuarios/nuevo' element={<CrearUsuarioV2 />} />
                        <Route path='/usuarios/carga-masiva' element={<CargaMasivaUsuariosV2 />} />
                        <Route path='/usuarios/:id' element={<EditarUsuarioV2 />} />
                        {/* Grupos (grupos-aa003-siigo): paridad con AA-001. Mismas
                            pantallas; /groupsV2 admite AA-003 (requireAA001or003) y
                            fuerza el idc del token. */}
                        <Route path='/usuarios/grupos' element={<GruposV2 />} />
                        <Route path='/usuarios/grupos/creador' element={<EditorGrupoV2 />} />
                        <Route path='/usuarios/grupos/:id' element={<EditorGrupoV2 />} />
                        {/* === DATA REPAIR FEATURE — eliminar cuando se termine === */}
                        <Route path='/repair/productos' element={<DataRepairProductsV2 />} />
                        <Route path='/repair/usuarios-fechas' element={<DataRepairUsersDatesV2 />} />
                        <Route path='/repair/reasignaciones' element={<DataRepairReassignmentsV2 />} />
                        <Route path='/repair/usuarios-bajas' element={<DataRepairUsersDatesDeleteV2 />} />
                        <Route path='/repair/dates-orden' element={<DataRepairOrderDatesV2 />} />
                        <Route path='/repair/ordenes-bajas' element={<DataRepairOrdersDatesDeleteV2 />} />
                        <Route path='/repair/ordenes-fechas' element={<DataRepairOrdersDatesV2 />} />
                        <Route path='/reportes' element={<ReportesV2 />} />
                        <Route path='/reportes/reasignaciones' element={<ReporteReasignacionV2 />} />
                        <Route path='/reportes/creacion-ordenes' element={<ReporteCreacionOrdenesV2 />} />
                        <Route path='/reportes/baja-ordenes' element={<ReporteBajaOrdenesV2 />} />
                        <Route path='/reportes/saldo-kits' element={<ReporteSaldoKitsV2 />} />
                        <Route path='/inventario/asignado' element={<StockAsignadoV2 />} />
                        <Route path='/inventario/propio' element={<StockPropioV2 />} />
                        <Route path='/movimientos/balance' element={<BalanceV2 />} />
                        <Route path='/soporte' element={<SoporteV2 />} />
                        <Route path='/soporte/:id' element={<ConversacionSoporteV2 />} />
                        <Route path='/manuales' element={<ManualesAdminV2 />} />
                        <Route path='/notificaciones' element={<NotificacionesV2 />} />
                        <Route path='/perfil' element={<PerfilV2 user={user} />} />
                        <Route path='/' element={<DashboardAdminV2 user={user} />} />
                        <Route path='*' element={<NotFound home="/" />} />
                    </Routes>
                </PrivateLayoutV2>
            </Suspense>
        );
    }
};

/**
 * PrivateRouter con guards declarativos:
 *  1. ErrorBoundary → captura crashes
 *  2. RequireCoordinates → pide dirección si falta location_cordena
 *  3. RequireNoPendingKits → muestra pantalla de kits pendientes
 *  4. PrivateRouterInner → renderiza las rutas
 * Excepción: un SA con la puerta del doble factor en "checking"/"required", o en la pantalla de
 * doble factor, salta 2 y 3 (no ve nada del portal hasta configurar el factor).
 */
const PrivateRouter = () => {
    const { user, mfaGate } = useAuth();
    const { setLoad } = useIdContext();
    const location = useLocation();

    // SA con la puerta del doble factor sin decidir o cerrada (o en la propia pantalla): directo a
    // la bifurcación SA, sin el formulario de dirección ni los kits pendientes (ver saSkipsPortalGuards).
    if (saSkipsPortalGuards(user, mfaGate, location.pathname)) {
        return (
            <ErrorBoundary>
                <PrivateRouterInner />
            </ErrorBoundary>
        );
    }

    return (
        <ErrorBoundary>
            <RequireCoordinates
                fallback={
                    <MissingCoordinatesFallback
                        user={user}
                        setLoad={setLoad}
                        setIsOpenCreate={() => { }}
                    />
                }
            >
                <RequireNoPendingKits renderFallback={PendingKitsFallback}>
                    <PrivateRouterInner />
                </RequireNoPendingKits>
            </RequireCoordinates>
        </ErrorBoundary>
    );
};

export default PrivateRouter;
